import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  alpha,
  Tooltip,
  Paper,
  Divider
} from '@mui/material';
import {
  Close,
  Delete,
  Security,
  Storage,
  CheckCircle,
  Cancel,
  Warning
} from '@mui/icons-material';
import { User } from '../types/user';
import { Database, UserDatabaseAccessSummary, DatabaseAccess } from '../types/admin';
import {
  fetchUserDatabaseAccess,
  fetchDatabases,
  fetchClients,
  grantDatabaseAccess,
  updateDatabaseAccess,
  revokeDatabaseAccess,
  revokeAllUserDatabaseAccess
} from '../api/admin';

const THEME = {
  BG_PRIMARY: "#000000",
  BG_SECONDARY: "#1a1a1a",
  BG_TERTIARY: "#2d2d2d",
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  TEXT_PRIMARY: "#ffffff",
  TEXT_SECONDARY: "#b0b0b0",
  TEXT_MUTED: "#808080",
  ACCENT_PRIMARY: "#2196f3",
  ACCENT_SUCCESS: "#4caf50",
  ACCENT_ERROR: "#ff4444",
  ACCENT_WARNING: "#ff9800",
  BORDER_SUBTLE: "#333333",
  BORDER_DEFAULT: "#555555",
};

interface DatabaseAccessDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
}

export const DatabaseAccessDialog: React.FC<DatabaseAccessDialogProps> = ({
  open,
  onClose,
  user
}) => {
  const [loading, setLoading] = useState(false);
  const [accessData, setAccessData] = useState<UserDatabaseAccessSummary | null>(null);
  const [allDatabases, setAllDatabases] = useState<Database[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Form state for adding new access
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<number>(0);
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Confirmation dialogs
  const [revokeConfirmOpen, setRevokeConfirmOpen] = useState(false);
  const [revokeAllConfirmOpen, setRevokeAllConfirmOpen] = useState(false);
  const [accessToRevoke, setAccessToRevoke] = useState<{ id: number; name: string } | null>(null);

  // Функция для безопасного извлечения сообщения об ошибке
  const extractErrorMessage = (err: any, defaultMessage: string): string => {
    const detail = err.response?.data?.detail;
    
    // Если detail - это строка, возвращаем её
    if (typeof detail === 'string') {
      return detail;
    }
    
    // Если detail - это массив ошибок валидации (FastAPI 422)
    if (Array.isArray(detail)) {
      return detail.map((error: any) => {
        if (typeof error === 'object' && error.msg) {
          return `${error.loc ? error.loc.join('.') + ': ' : ''}${error.msg}`;
        }
        return String(error);
      }).join('; ');
    }
    
    // Если detail - это объект
    if (typeof detail === 'object' && detail !== null) {
      return JSON.stringify(detail);
    }
    
    return defaultMessage;
  };

  const loadData = useCallback(async () => {
    if (!user.id) return;

    try {
      setLoading(true);
      setError(null);

      // Сначала загружаем все базы данных и клиентов
      let databases: Database[] = [];
      let clients: any[] = [];
      
      try {
        [databases, clients] = await Promise.all([
          fetchDatabases(),
          fetchClients()
        ]);
      } catch (dbErr: any) {
        // Если нет доступа к базам данных (403), продолжаем с пустым списком
        if (dbErr.response?.status === 403) {
          setError('⚠️ Accesso alle basi di dati limitato.');
        } else {
          throw dbErr;
        }
      }
      
      // Добавление имени клиента к каждой БД
      const databasesWithClientNames = databases.map(db => ({
        ...db,
        client_name: db.client_name || clients.find(c => c.id === db.client_id)?.name || 'N/A'
      }));
      
      // Фильтрация БД по client_id пользователя
      const clientDatabases = user.client_id
        ? databasesWithClientNames.filter(db => db.client_id === user.client_id)
        : databasesWithClientNames;
      
      setAllDatabases(clientDatabases);

      // Load user database access
      const access = await fetchUserDatabaseAccess(user.id);
      
      // Проверяем структуру ответа
      let accessSummary: UserDatabaseAccessSummary;
      
      if (Array.isArray(access)) {
        // Backend вернул массив доступов напрямую - подсчитываем статистику
        const databasesArray = access as DatabaseAccess[];
        accessSummary = {
          user_id: user.id,
          username: user.username,
          databases: databasesArray,
          accessible_databases: databasesArray.length,
          databases_with_write: databasesArray.filter(d => d.can_write).length,
          total_databases: clientDatabases.length
        };
      } else {
        // Backend вернул полный summary - используем его
        accessSummary = {
          ...access,
          databases: access.databases || [],
          accessible_databases: access.accessible_databases ?? (access.databases?.length || 0),
          total_databases: access.total_databases ?? clientDatabases.length,
          databases_with_write: access.databases_with_write ?? 0
        };
      }
      
      // Подклеиваем имя базы и клиента к каждому доступу, если их нет в ответе
      const enrichedDatabases = (accessSummary.databases || []).map((a) => {
        const matchedDb = clientDatabases.find(db => db.id === a.database_id);
        return {
          ...a,
          database_name: a.database_name || matchedDb?.name || `DB-${a.database_id}`,
          client_name: a.client_name || matchedDb?.client_name || matchedDb?.client_id ? matchedDb?.client_name || 'N/A' : 'N/A'
        } as DatabaseAccess;
      });

      accessSummary = {
        ...accessSummary,
        databases: enrichedDatabases
      };

      setAccessData(accessSummary);

    } catch (err: any) {
      setError(extractErrorMessage(err, 'Impossibile caricare i dati di accesso'));
    } finally {
      setLoading(false);
    }
  }, [user.id, user.client_id]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const handleGrantAccess = async () => {
    if (!selectedDatabaseId) {
      setError('Seleziona un database');
      return;
    }

    if (!canRead && !canWrite) {
      setError('Seleziona almeno un permesso (lettura o scrittura)');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);

      await grantDatabaseAccess({
        user_id: user.id,
        database_id: selectedDatabaseId,
        can_read: canRead,
        can_write: canWrite
      });

      setSuccess('Accesso concesso con successo');
      setSelectedDatabaseId(0);
      setCanRead(true);
      setCanWrite(false);
      await loadData();

    } catch (err: any) {
      const errorMsg = extractErrorMessage(err, 'Impossibile concedere l\'accesso');
      
      if (errorMsg.includes('already exists')) {
        setError('Accesso già esistente. Usa i toggle per modificare i permessi.');
        // Перезагружаем данные, чтобы показать существующий доступ
        await loadData();
      } else if (errorMsg.includes('different clients')) {
        setError('Utente e database devono appartenere allo stesso cliente');
      } else {
        setError(errorMsg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleTogglePermission = async (
    access: DatabaseAccess,
    field: 'can_read' | 'can_write'
  ) => {
    try {
      setError(null);
      setSuccess(null);

      const newValue = !access[field];
      const otherField = field === 'can_read' ? 'can_write' : 'can_read';

      // Проверка: хотя бы одна галочка должна быть
      if (!newValue && !access[otherField]) {
        setError('È richiesto almeno un permesso. Usa il pulsante elimina per revocare completamente l\'accesso.');
        return;
      }

      await updateDatabaseAccess(access.id, {
        can_read: field === 'can_read' ? newValue : access.can_read,
        can_write: field === 'can_write' ? newValue : access.can_write
      });

      setSuccess('Permessi aggiornati');
      await loadData();

    } catch (err: any) {
      setError(extractErrorMessage(err, 'Impossibile aggiornare i permessi'));
    }
  };

  const handleRevokeAccess = async (accessId: number, databaseName: string) => {
    setAccessToRevoke({ id: accessId, name: databaseName });
    setRevokeConfirmOpen(true);
  };

  const confirmRevokeAccess = async () => {
    if (!accessToRevoke) return;

    try {
      setError(null);
      setSuccess(null);
      setRevokeConfirmOpen(false);

      await revokeDatabaseAccess(accessToRevoke.id);
      setSuccess('Accesso revocato con successo');
      await loadData();

    } catch (err: any) {
      setError(extractErrorMessage(err, 'Impossibile revocare l\'accesso'));
    } finally {
      setAccessToRevoke(null);
    }
  };

  const handleRevokeAll = async () => {
    if (!accessData || accessData.accessible_databases === 0) return;
    setRevokeAllConfirmOpen(true);
  };

  const confirmRevokeAll = async () => {
    try {
      setError(null);
      setSuccess(null);
      setSubmitting(true);
      setRevokeAllConfirmOpen(false);

      await revokeAllUserDatabaseAccess(user.id);
      setSuccess(`Tutti gli accessi revocati (${accessData?.accessible_databases} database)`);
      await loadData();

    } catch (err: any) {
      setError(extractErrorMessage(err, 'Impossibile revocare tutti gli accessi'));
    } finally {
      setSubmitting(false);
    }
  };

  // Базы данных без доступа (доступные для добавления)
  const accessibleDbIds = accessData?.databases?.map(a => a.database_id) || [];
  const availableToAdd = allDatabases.filter(db => !accessibleDbIds.includes(db.id));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: THEME.BG_SECONDARY,
          border: `1px solid ${THEME.BORDER_SUBTLE}`,
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: THEME.BG_PRIMARY,
          borderBottom: `1px solid ${THEME.BORDER_SUBTLE}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Security sx={{ color: THEME.ACCENT_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h6" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
               Gestione Accesso Database
            </Typography>
            <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY, mt: 0.5 }}>
              Utente: {user.full_name} ({user.username})
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: THEME.TEXT_SECONDARY }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: THEME.ACCENT_PRIMARY }} />
          </Box>
        ) : (
          <>
            {/* Alerts */}
            {error && (
              <Alert
                severity="error"
                onClose={() => setError(null)}
                sx={{ mb: 2 }}
                icon={<Warning />}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                onClose={() => setSuccess(null)}
                sx={{ mb: 2 }}
                icon={<CheckCircle />}
              >
                {success}
              </Alert>
            )}

            {/* Statistics */}
            {accessData && (
              <Paper
                sx={{
                  p: 2,
                  mb: 3,
                  bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1),
                  border: `1px solid ${alpha(THEME.ACCENT_PRIMARY, 0.3)}`
                }}
              >
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<Storage />}
                    label={`${accessData.accessible_databases ?? 0} di ${accessData.total_databases ?? 0} database`}
                    sx={{
                      bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2),
                      color: THEME.ACCENT_PRIMARY,
                      fontWeight: 600
                    }}
                  />
                  <Chip
                    icon={<CheckCircle />}
                    label={`${accessData.databases_with_write ?? 0} con permesso di scrittura`}
                    sx={{
                      bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.2),
                      color: THEME.ACCENT_SUCCESS,
                      fontWeight: 600
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* Current Access Table */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h6"
                sx={{ color: THEME.TEXT_PRIMARY, mb: 2, fontWeight: 600 }}
              >
                📋 Accessi Correnti
              </Typography>

              {accessData && accessData.databases && accessData.databases.length > 0 ? (
                <TableContainer
                  component={Paper}
                  sx={{
                    bgcolor: THEME.BG_PRIMARY,
                    border: `1px solid ${THEME.BORDER_SUBTLE}`
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: THEME.BG_TERTIARY }}>
                        <TableCell sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                          Database
                        </TableCell>
                        <TableCell sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                          Cliente
                        </TableCell>
                        <TableCell align="center" sx={{ color: THEME.ACCENT_SUCCESS, fontWeight: 600 }}>
                          Lettura
                        </TableCell>
                        <TableCell align="center" sx={{ color: THEME.ACCENT_WARNING, fontWeight: 600 }}>
                          Scrittura
                        </TableCell>
                        <TableCell align="center" sx={{ color: THEME.TEXT_PRIMARY, fontWeight: 600 }}>
                          Azioni
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {accessData.databases.map((access) => (
                        <TableRow
                          key={access.id}
                          sx={{
                            '&:hover': { bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.05) }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Storage sx={{ color: '#2196f3', fontSize: 20 }} />
                              <span style={{ color: '#2196f3', fontWeight: 700, fontSize: '1rem' }}>
                                {access.database_name}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.9rem' }}>
                            {access.client_name || 'N/A'}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Toggle permesso lettura">
                              <Checkbox
                                checked={access.can_read}
                                onChange={() => handleTogglePermission(access, 'can_read')}
                                sx={{
                                  color: THEME.TEXT_SECONDARY,
                                  '&.Mui-checked': { color: THEME.ACCENT_SUCCESS }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Toggle permesso scrittura">
                              <Checkbox
                                checked={access.can_write}
                                onChange={() => handleTogglePermission(access, 'can_write')}
                                sx={{
                                  color: THEME.TEXT_SECONDARY,
                                  '&.Mui-checked': { color: THEME.ACCENT_WARNING }
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Revoca accesso">
                              <IconButton
                                size="small"
                                onClick={() => handleRevokeAccess(access.id, access.database_name || 'БД')}
                                sx={{
                                  color: THEME.ACCENT_ERROR,
                                  '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.1) }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Paper
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    bgcolor: alpha(THEME.TEXT_SECONDARY, 0.05),
                    border: `1px dashed ${THEME.BORDER_SUBTLE}`
                  }}
                >
                  <Cancel sx={{ fontSize: 48, color: THEME.TEXT_SECONDARY, mb: 1 }} />
                  <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
                    Nessun accesso al database ancora concesso
                  </Typography>
                </Paper>
              )}
            </Box>

            <Divider sx={{ my: 3, borderColor: THEME.BORDER_SUBTLE }} />

            {/* Add New Access Form */}
            {availableToAdd.length > 0 && (
              <Paper
                sx={{
                  p: 3,
                  bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.05),
                  border: `1px solid ${alpha(THEME.ACCENT_SUCCESS, 0.2)}`
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ color: THEME.TEXT_PRIMARY, mb: 2, fontWeight: 600 }}
                >
                  ➕ Aggiungi Accesso Database
                </Typography>

                <FormControl
                  fullWidth
                  sx={{
                    mb: 2,
                    '& .MuiOutlinedInput-root': {
                      bgcolor: THEME.SURFACE_GLASS,
                      color: THEME.TEXT_PRIMARY,
                      '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                      '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                      '&.Mui-focused fieldset': { borderColor: THEME.TEXT_PRIMARY }
                    },
                    '& .MuiInputLabel-root': {
                      color: THEME.TEXT_MUTED,
                      '&.Mui-focused': { color: THEME.ACCENT_PRIMARY }
                    }
                  }}
                >
                  <InputLabel>Seleziona Database</InputLabel>
                  <Select
                    value={selectedDatabaseId}
                    onChange={(e) => setSelectedDatabaseId(Number(e.target.value))}
                    label="Seleziona Database"
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          bgcolor: THEME.BG_SECONDARY,
                          border: `1px solid ${THEME.BORDER_SUBTLE}`,
                          '& .MuiMenuItem-root': {
                            color: THEME.TEXT_PRIMARY,
                            '&:hover': {
                              bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.1)
                            },
                            '&.Mui-selected': {
                              bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.15),
                              '&:hover': {
                                bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.2)
                              }
                            }
                          }
                        }
                      }
                    }}
                  >
                    <MenuItem value={0} disabled>
                        -- Seleziona un Database --
                    </MenuItem>
                    {availableToAdd.map((db) => (
                      <MenuItem key={db.id} value={db.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <Typography component="span" sx={{ fontWeight: 600, color: THEME.TEXT_PRIMARY }}>
                            {db.name}
                          </Typography>
                          <Typography component="span" sx={{ color: THEME.TEXT_SECONDARY, fontSize: '0.9rem' }}>
                            ({db.client_name})
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={canRead}
                        onChange={(e) => setCanRead(e.target.checked)}
                        sx={{
                          color: THEME.TEXT_SECONDARY,
                          '&.Mui-checked': { color: THEME.ACCENT_SUCCESS }
                        }}
                      />
                    }
                    label="Lettura"
                    sx={{ 
                      color: THEME.ACCENT_SUCCESS,
                      fontWeight: 500,
                      '& .MuiTypography-root': { 
                        color: THEME.ACCENT_SUCCESS 
                      }
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={canWrite}
                        onChange={(e) => setCanWrite(e.target.checked)}
                        sx={{
                          color: THEME.TEXT_SECONDARY,
                          '&.Mui-checked': { color: THEME.ACCENT_WARNING }
                        }}
                      />
                    }
                    label="Scrittura"
                    sx={{ 
                      color: THEME.ACCENT_WARNING,
                      fontWeight: 500,
                      '& .MuiTypography-root': { 
                        color: THEME.ACCENT_WARNING 
                      }
                    }}
                  />
                </Box>

                <Button
                  variant="contained"
                  onClick={handleGrantAccess}
                  disabled={!selectedDatabaseId || submitting || (!canRead && !canWrite)}
                  sx={{
                    bgcolor: THEME.ACCENT_SUCCESS,
                    '&:hover': { bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.8) },
                    '&:disabled': {
                      bgcolor: alpha(THEME.TEXT_SECONDARY, 0.2),
                      color: THEME.TEXT_SECONDARY
                    }
                  }}
                >
                  {submitting ? <CircularProgress size={24} /> : '➕ Concedi Accesso'}
                </Button>
              </Paper>
            )}

            {availableToAdd.length === 0 && accessData && accessData.databases && accessData.databases.length > 0 && (
              <Alert severity="info" icon={<CheckCircle />}>
                L'utente ha accesso a tutti i database disponibili
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: `1px solid ${THEME.BORDER_SUBTLE}`,
          bgcolor: THEME.BG_PRIMARY,
          justifyContent: 'space-between'
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            color: THEME.TEXT_SECONDARY,
            '&:hover': { bgcolor: alpha(THEME.TEXT_SECONDARY, 0.1) }
          }}
        >
          Chiudi
        </Button>

        {accessData && accessData.databases && accessData.databases.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleRevokeAll}
            disabled={submitting}
            startIcon={<Delete />}
            sx={{
              bgcolor: THEME.ACCENT_ERROR,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.8) }
            }}
          >
            {submitting ? <CircularProgress size={24} /> : 'Revoca Tutti gli Accessi'}
          </Button>
        )}
      </DialogActions>

      {/* Confirm Revoke Single Access Dialog */}
      <Dialog
        open={revokeConfirmOpen}
        onClose={() => {
          setRevokeConfirmOpen(false);
          setAccessToRevoke(null);
        }}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Conferma Revoca Accesso
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
            Sei sicuro di voler revocare l'accesso al database <strong>"{accessToRevoke?.name}"</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              setRevokeConfirmOpen(false);
              setAccessToRevoke(null);
            }}
            sx={{ color: THEME.TEXT_SECONDARY }}
          >
            Annulla
          </Button>
          <Button
            onClick={confirmRevokeAccess}
            variant="contained"
            color="error"
            sx={{
              bgcolor: THEME.ACCENT_ERROR,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.8) }
            }}
          >
            Revoca
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Revoke All Access Dialog */}
      <Dialog
        open={revokeAllConfirmOpen}
        onClose={() => setRevokeAllConfirmOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ color: THEME.TEXT_PRIMARY }}>
          Conferma Revoca Tutti gli Accessi
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            Questa azione è irreversibile!
          </Alert>
          <Typography sx={{ color: THEME.TEXT_SECONDARY }}>
            Sei sicuro di voler revocare <strong>TUTTI gli accessi</strong> ai database per l'utente <strong>"{user.username}"</strong>?
            <br /><br />
            Verrà revocato l'accesso a <strong>{accessData?.accessible_databases}</strong> database.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setRevokeAllConfirmOpen(false)}
            sx={{ color: THEME.TEXT_SECONDARY }}
            disabled={submitting}
          >
            Annulla
          </Button>
          <Button
            onClick={confirmRevokeAll}
            variant="contained"
            color="error"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : <Delete />}
            sx={{
              bgcolor: THEME.ACCENT_ERROR,
              '&:hover': { bgcolor: alpha(THEME.ACCENT_ERROR, 0.8) }
            }}
          >
            {submitting ? 'Revoca in corso...' : 'Revoca Tutti'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
